import { type RelayStatus } from './types';

/**
 * Pure parsing of the admin bid firehose (`GET /api/bids/stream`).
 *
 * The stream carries unnamed `data: <json>` broadcasts of several shapes on
 * the same Redis channel (`sse:admin:bids`):
 * - MongoDB change-stream frames — `{operationType, fullDocument,
 *   updateDescription, carBrand, carModel}` (BidChangeStreamService publishes
 *   the raw change's `updateDescription`, so an update carries
 *   `updateDescription.updatedFields.<field>`; `fullDocument` is present on
 *   updates too thanks to `fullDocument: 'updateLookup'`). MarkWonService and
 *   the auction engine emit the same shape through the outbox for the writes
 *   they own.
 * - relay audit rows — `{type: 'relay.queued' | 'relay.confirmed' |
 *   'relay.outcome' | 'relay.cancelled', data: {bidId, carId, who,
 *   previousRelayStatus, newRelayStatus, …}}` (contracts `RelayAuditEventSchema`,
 *   appended by RelayQueueService.executeTransition in the SAME transaction as
 *   the relayStatus flip). With `OUTBOX_ENABLED=true` the change stream idles
 *   and these are the ONLY frames a queue / confirm / cancel produces.
 * - the platform's auto-cancel — a FLAT `{bidId, carId, previousRelayStatus,
 *   newRelayStatus: 'cancelled', reason, when}` with no `type` and no `data`
 *   (auction-engine `relayAutoCancelledEvent`).
 * - other admin audit rows — `{type: 'admin.*', data: {...}}` — and named
 *   user-facing broadcasts (`{type: 'bid-status-change', …}`), which the
 *   operator must NOT be alarmed by.
 * The outbox dispatcher adds an `eventId` to everything it publishes, and a
 * `carBrand`/`carModel` to any admin frame that carries only a `carId`.
 *
 * Two events matter to the operator: a change-stream INSERT ("new bid landed")
 * and a relayStatus transition, whichever frame shape carried it. Kept free of
 * React and EventSource so the discrimination is unit-testable.
 */

/** A newly captured client bid, as the relay desk needs to see it. */
export interface NewBidEvent {
  bidId: string;
  carId: string;
  /** "BMW X5" when the change stream enriched the frame, else null. */
  carLabel: string | null;
  bidAmount: number;
  receivedAt: Date;
}

/** A bid whose relay pipeline state changed — by an operator or by the platform. */
export interface RelayChangeEvent {
  kind: 'relay-change';
  bidId: string;
  carId: string | null;
  carLabel: string | null;
  relayStatus: RelayStatus;
  /**
   * Admin id that performed THIS transition, when the frame says so — only the
   * relay audit row does (`data.who`). A change-stream frame yields null: its
   * `fullDocument.relayedBy` names who once CONFIRMED the relay, which is not
   * who queued or cancelled the bid just now, so it is deliberately not read.
   * A platform auto-cancel yields null too — nobody clicked.
   */
  actorId: string | null;
  receivedAt: Date;
}

/**
 * A bid whose AMOUNT changed — the client raised their own bid, or a proxy
 * defended it (BUG-20, e2e-bugfix-2). On the capture path the same update
 * re-sets `relayStatus: 'captured'`, so read as a relay transition it toasted
 * «статус змінено на «Ставку прийнято»» for a bid whose status never moved.
 */
export interface AmountChangedEvent {
  kind: 'amount-changed';
  bidId: string;
  carId: string | null;
  carLabel: string | null;
  bidAmount: number;
  receivedAt: Date;
}

export type AdminStreamEvent =
  | ({ kind: 'new-bid' } & NewBidEvent)
  | RelayChangeEvent
  | AmountChangedEvent;

/** Who, relative to the signed-in admin, performed a relay transition. */
export type RelayChangeAudience = 'self' | 'other' | 'unknown';

const RELAY_STATUSES: ReadonlySet<string> = new Set<RelayStatus>([
  'captured',
  'queued',
  'relayed',
  'won',
  'lost',
  'cancelled',
  // Phase 10 tender-close cron; was missing here, so an `expired` frame was
  // silently dropped instead of announced.
  'expired',
  // Phase 13: the upstream auction refused the bid.
  'upstream_rejected',
]);

/** Audit `type`s that carry a relayStatus transition (contracts RelayAuditEventSchema). */
const RELAY_TRANSITION_AUDIT_TYPES: ReadonlySet<string> = new Set([
  'relay.queued',
  'relay.confirmed',
  'relay.outcome',
  'relay.cancelled',
  // Phase 13 (relayed|queued → upstream_rejected).
  'relay.rejected',
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asRelayStatus(value: unknown): RelayStatus | null {
  return typeof value === 'string' && RELAY_STATUSES.has(value) ? (value as RelayStatus) : null;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * The API's "car not found" placeholders (`BidChangeStreamService.getCarInfo`,
 * `carEnrichmentOf`). They are not a car name, so they never reach a toast.
 */
const PLACEHOLDER_BRAND = 'Unknown';
const PLACEHOLDER_MODEL = 'Model';

/** Join the enriched brand/model into a display label, or null when absent. */
export function carLabelOf(brand: unknown, model: unknown): string | null {
  const parts = [
    brand === PLACEHOLDER_BRAND ? null : brand,
    model === PLACEHOLDER_MODEL ? null : model,
  ].filter((part): part is string => typeof part === 'string' && part !== '');
  return parts.length > 0 ? parts.join(' ') : null;
}

/** One raw SSE `data:` payload as a JSON object, or null for anything else. */
function parseFrame(raw: string): Record<string, unknown> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return asRecord(parsed);
}

/**
 * The change-stream frame a raw payload carries, or null for anything that is
 * not one (audit row, named broadcast, keepalive, malformed JSON).
 */
function asChangeFrame(frame: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!frame) return null;
  // Audit rows and named user-facing broadcasts both carry a `type`; neither
  // is a change-stream frame.
  if (typeof frame.type === 'string') return null;
  return typeof frame.operationType === 'string' ? frame : null;
}

/**
 * Turn one raw SSE `data:` payload into a NewBidEvent, or null when the frame
 * is anything else (audit row, update, delete, keepalive, malformed JSON).
 */
export function parseNewBidEvent(raw: string, now: Date = new Date()): NewBidEvent | null {
  const frame = asChangeFrame(parseFrame(raw));
  if (!frame || frame.operationType !== 'insert') return null;

  const document = asRecord(frame.fullDocument);
  if (!document) return null;

  const bidId = document._id;
  const carId = document.carId;
  if (typeof bidId !== 'string' || typeof carId !== 'string') return null;

  return {
    bidId,
    carId,
    carLabel: carLabelOf(frame.carBrand, frame.carModel),
    bidAmount: typeof document.bid === 'number' ? document.bid : 0,
    receivedAt: now,
  };
}

/** Change-stream UPDATE whose `updatedFields.relayStatus` is a known relay state. */
function relayChangeFromChangeStream(
  frame: Record<string, unknown>,
  now: Date,
): RelayChangeEvent | null {
  if (frame.operationType !== 'update') return null;

  const updatedFields = asRecord(asRecord(frame.updateDescription)?.updatedFields);
  const relayStatus = asRelayStatus(updatedFields?.relayStatus);
  if (!relayStatus) return null;

  const document = asRecord(frame.fullDocument);
  const documentKey = asRecord(frame.documentKey);
  const bidId = document?._id ?? documentKey?._id;
  if (typeof bidId !== 'string') return null;

  return {
    kind: 'relay-change',
    bidId,
    carId: asNonEmptyString(document?.carId),
    carLabel: carLabelOf(frame.carBrand, frame.carModel),
    relayStatus,
    actorId: null,
    receivedAt: now,
  };
}

/**
 * Change-stream UPDATE whose `updatedFields.bid` is a number — an amount
 * change. Only a bid write (raise, proxy defence) sets `bid`; a relay
 * transition or a status flip never does, so the field alone is the
 * discriminator. Checked BEFORE the relay-change reading: the capture path
 * writes `relayStatus: 'captured'` in the same update, and that re-entry is
 * not a transition the operator should hear about — the amount is the news.
 */
function amountChangeFromChangeStream(
  frame: Record<string, unknown>,
  now: Date,
): AmountChangedEvent | null {
  if (frame.operationType !== 'update') return null;

  const updatedFields = asRecord(asRecord(frame.updateDescription)?.updatedFields);
  const bidAmount = updatedFields?.bid;
  if (typeof bidAmount !== 'number' || Number.isNaN(bidAmount)) return null;

  const document = asRecord(frame.fullDocument);
  const documentKey = asRecord(frame.documentKey);
  const bidId = document?._id ?? documentKey?._id;
  if (typeof bidId !== 'string') return null;

  return {
    kind: 'amount-changed',
    bidId,
    carId: asNonEmptyString(document?.carId),
    carLabel: carLabelOf(frame.carBrand, frame.carModel),
    bidAmount,
    receivedAt: now,
  };
}

/**
 * Turn one raw SSE `data:` payload into an AmountChangedEvent, or null when
 * the frame is anything else.
 */
export function parseAmountChangedEvent(
  raw: string,
  now: Date = new Date(),
): AmountChangedEvent | null {
  const frame = asChangeFrame(parseFrame(raw));
  return frame ? amountChangeFromChangeStream(frame, now) : null;
}

/** `{type: 'relay.*', data: {...}}` audit row for a relayStatus transition. */
function relayChangeFromAuditRow(
  frame: Record<string, unknown>,
  now: Date,
): RelayChangeEvent | null {
  if (typeof frame.type !== 'string' || !RELAY_TRANSITION_AUDIT_TYPES.has(frame.type)) return null;

  const data = asRecord(frame.data);
  if (!data) return null;

  const bidId = asNonEmptyString(data.bidId);
  const relayStatus = asRelayStatus(data.newRelayStatus);
  if (!bidId || !relayStatus) return null;

  return {
    kind: 'relay-change',
    bidId,
    carId: asNonEmptyString(data.carId),
    // The outbox dispatcher adds carBrand/carModel next to `data` (BUG-20).
    carLabel: carLabelOf(frame.carBrand, frame.carModel),
    relayStatus,
    actorId: asNonEmptyString(data.who),
    receivedAt: now,
  };
}

/**
 * The platform's flat auto-cancel frame (`relayAutoCancelledEvent`): no `type`,
 * no `operationType`, no `data` — matched on its own three load-bearing fields
 * so nothing else flat on the channel can masquerade as a transition.
 */
function relayChangeFromAutoCancel(
  frame: Record<string, unknown>,
  now: Date,
): RelayChangeEvent | null {
  if (typeof frame.type === 'string' || typeof frame.operationType === 'string') return null;

  const bidId = asNonEmptyString(frame.bidId);
  const relayStatus = asRelayStatus(frame.newRelayStatus);
  const reason = asNonEmptyString(frame.reason);
  if (!bidId || !relayStatus || !reason) return null;

  return {
    kind: 'relay-change',
    bidId,
    carId: asNonEmptyString(frame.carId),
    carLabel: carLabelOf(frame.carBrand, frame.carModel),
    relayStatus,
    actorId: null,
    receivedAt: now,
  };
}

/**
 * Turn one raw SSE `data:` payload into a RelayChangeEvent — a relayStatus
 * transition in ANY of the shapes the channel carries (change-stream update,
 * `relay.*` audit row, platform auto-cancel). Any other frame returns null.
 */
export function parseRelayChangeEvent(raw: string, now: Date = new Date()): RelayChangeEvent | null {
  const frame = parseFrame(raw);
  if (!frame) return null;

  const changeFrame = asChangeFrame(frame);
  if (changeFrame) return relayChangeFromChangeStream(changeFrame, now);

  return relayChangeFromAuditRow(frame, now) ?? relayChangeFromAutoCancel(frame, now);
}

/**
 * One entry point for the stream hook: whichever operator-relevant event the
 * frame is. An amount change wins over a relay change on the same update
 * frame (BUG-20) — see `amountChangeFromChangeStream`.
 */
export function parseAdminStreamEvent(raw: string, now: Date = new Date()): AdminStreamEvent | null {
  const newBid = parseNewBidEvent(raw, now);
  if (newBid) return { kind: 'new-bid', ...newBid };
  return parseAmountChangedEvent(raw, now) ?? parseRelayChangeEvent(raw, now);
}

/**
 * Who did this, relative to the signed-in admin. 'self' ONLY when the frame
 * names the actor and it is this admin — that is the one case the hook stays
 * quiet (the mutation's own toast already spoke). Everything else is announced:
 * 'other' with the colleague's name in the wording, 'unknown' (no actor on the
 * frame, or the session id not yet resolved) in neutral wording. A spurious
 * toast is cheaper than a missing one on a relay desk.
 */
export function relayChangeAudience(
  event: RelayChangeEvent,
  selfAdminId: string | undefined,
): RelayChangeAudience {
  if (!event.actorId || !selfAdminId) return 'unknown';
  return event.actorId === selfAdminId ? 'self' : 'other';
}

/**
 * Merge a new event into the visible queue, newest last, deduplicated by bid
 * id and capped so a burst cannot grow the banner without bound.
 */
export function appendNewBidEvent(
  queue: NewBidEvent[],
  event: NewBidEvent,
  cap = 5,
): NewBidEvent[] {
  if (queue.some((existing) => existing.bidId === event.bidId)) return queue;
  return [...queue, event].slice(-cap);
}
